import {
  attr,
  Fragment,
  html,
  MapSignal,
  on,
  OnDispose,
  prop,
} from '@tempots/dom'
import { savedDesignSchema } from '@rh/shared/schemas/design'
import { sanitizeDesign } from '@rh/shared/utils/sanitize-design-html'
import { AgencyAssetService } from '@/services/agency-asset-service'
import { tokenFieldByToken } from '../../token-fields'
import type { WizardProfile, WizardState } from '../wizard-state'
import type {
  ExtractedHeaderModel,
  ExtractedImage,
  SuggestionScope,
  TokenKind,
  TokenSuggestion,
} from '../pdf-extract/types'
import {
  importWriteBackIntents,
  KIND_FIELD,
  savedValueFor,
} from '../pdf-extract/classify'
import { buildDesignFromExtraction } from '../pdf-extract/build-design'

// ============================================================
// Review step, import path — a RECEIPT, not a questionnaire.
// Every detection shows as one row: the found text plus a pill
// stating the decision already made for it (defaults are safe:
// click-through never changes rendered text or overwrites saved
// data). The pill opens a compact change-menu, the same gesture
// as the editor's chips.
// ============================================================

/** Plain words for each detection kind, used mid-sentence. */
const KIND_NOUN: Record<TokenKind, string> = {
  email: 'email',
  phone: 'phone number',
  url: 'website',
  linkedin: 'LinkedIn',
  name: 'name',
}

const scopeLabel = (scope: SuggestionScope, kind: TokenKind): string =>
  tokenFieldByToken(`{{${scope}.${KIND_FIELD[kind]}}}`)?.label ??
  `${scope} ${KIND_NOUN[kind]}`

/** Which scopes a detection may fill from ({{recruiter.url}} isn't a thing). */
const scopesFor = (kind: TokenKind): SuggestionScope[] =>
  kind === 'url' ? ['agency'] : ['agency', 'recruiter']

// Object URLs for the cropped logo previews, keyed by blob so re-renders
// (decision toggles bump `rev`) reuse them instead of leaking new ones.
const logoPreviewUrls = new WeakMap<Blob, string>()

function logoPreviewUrl(blob: Blob): string {
  let url = logoPreviewUrls.get(blob)
  if (!url) {
    url = URL.createObjectURL(blob)
    logoPreviewUrls.set(blob, url)
  }
  return url
}

export function ReviewStep(state: WizardState, agencyId: string) {
  const model = state.extraction.value
  if (!model) {
    state.step.set('upload')
    return html.span()
  }
  // Bump to re-render after mutating the model in place (choice changes,
  // block exclusion). The model object is step-local working state, not
  // app state — a counter keeps this simple.
  const rev = prop(0)
  const working = prop(false)
  const error = prop<string | null>(null)

  const finish = async () => {
    if (working.value) return
    closeReviewMenu()
    working.set(true)
    error.set(null)
    try {
      // Upload logo crops (relaxed 'crop' processing keeps them sharp).
      const logoAssetKeys = new Map<ExtractedImage, string>()
      for (const band of model.bands) {
        for (const image of band.images) {
          const file = new File([image.blob], 'imported-logo.png', {
            type: 'image/png',
          })
          const res = await AgencyAssetService.uploadDesignImageAsset(
            agencyId,
            file,
            { variant: 'crop' }
          )
          logoAssetKeys.set(image, res.assetKey)
        }
      }
      const design = sanitizeDesign(
        savedDesignSchema.parse(buildDesignFromExtraction(model, logoAssetKeys))
      )
      state.writeBacks.set(importWriteBackIntents(model, state.profile.value))
      state.result.set(design)
      state.step.set('preview')
    } catch (e) {
      console.error('import build failed', e)
      error.set(
        'We couldn’t turn this into a design. You can go Back to adjust the areas, or build from your info instead.'
      )
    } finally {
      working.set(false)
    }
  }

  return html.div(
    // A floating menu must never outlive the step (Back, close, errors).
    OnDispose(closeReviewMenu),
    MapSignal(rev, () => renderModel(model, state.profile.value, rev)),
    model.warnings.includes('rotated-text-skipped')
      ? html.p(
          attr.class('wiz-hint'),
          'Note: some rotated text was skipped — you can add it in the editor.'
        )
      : html.span(),
    MapSignal(error, err =>
      err
        ? Fragment(
            html.div(attr.class('wiz-error'), err),
            html.button(
              attr.type('button'),
              attr.class('wiz-btn wiz-btn-secondary'),
              attr.style('margin-top:10px;'),
              on.click(() => {
                state.path.set('build')
                state.step.set('shape')
              }),
              'Build from my info instead'
            )
          )
        : html.span()
    ),
    html.div(
      attr.style('margin-top:16px;'),
      MapSignal(working, w =>
        html.button(
          attr.type('button'),
          attr.class('wiz-btn wiz-btn-primary'),
          attr.disabled(w),
          on.click(() => void finish()),
          w ? 'Building…' : 'Looks good — preview →'
        )
      )
    )
  )
}

const quoted = (text: string) =>
  html.span(attr.class('wiz-decision-found'), `“${text}”`)

interface PillState {
  linked: boolean
  scope?: SuggestionScope
  label: string
  /** One short consequence clause; '' when the label says it all. */
  note: string
  /** Note conveys a caution (blank render) rather than information. */
  warn?: boolean
}

/** The one-line summary of what this suggestion will do as configured. */
function pillState(s: TokenSuggestion, profile: WizardProfile): PillState {
  if (s.choice === 'text') {
    return { linked: false, label: 'kept as typed', note: '' }
  }
  const label = scopeLabel(s.choice, s.kind)
  const value = s.matchedText.trim()
  const saved = savedValueFor(s.kind, s.choice, profile)?.trim()
  const base = { linked: true, scope: s.choice, label }

  if (s.match !== 'none' && s.matchedScope === s.choice) {
    return {
      ...base,
      note: s.match === 'exact' ? '✓ matches your profile' : `shows “${saved}”`,
    }
  }
  if (s.choice === 'agency') {
    if (s.kind === 'name') {
      return { ...base, note: `shows “${profile.agencyName}”` }
    }
    if (!saved) return { ...base, note: 'will save to your Agency profile' }
    if (saved === value) return { ...base, note: '✓ matches your profile' }
    return s.updateSaved
      ? { ...base, note: `updates your profile (was “${saved}”)` }
      : { ...base, note: `shows “${saved}”` }
  }
  // recruiter
  if (saved === value) return { ...base, note: '✓ matches your Profile' }
  if (s.saveAsMine) {
    return {
      ...base,
      note: saved ? 'updates your Profile' : 'will save to your Profile',
    }
  }
  return saved
    ? { ...base, note: `you’ll show “${saved}”` }
    : { ...base, note: 'blank until your Profile has one', warn: true }
}

// ── Floating change-menu ─────────────────────────────────────
// A fixed-position popover on document.body, exactly like the editor's
// chip menu: opening it reflows NOTHING, so the modal never grows or
// shifts and the user keeps their visual place. Built imperatively
// (textContent only — matched text comes from the uploaded document).

let activeMenu: { el: HTMLElement; suggestion: TokenSuggestion } | null = null

function closeReviewMenu() {
  activeMenu?.el.remove()
  activeMenu = null
  document.removeEventListener('mousedown', onOutsideMousedown, true)
  document.removeEventListener('keydown', onMenuEscape, true)
  document.removeEventListener('scroll', closeReviewMenu, true)
}

function onOutsideMousedown(e: MouseEvent) {
  if (activeMenu && !activeMenu.el.contains(e.target as Node)) {
    closeReviewMenu()
  }
}

function onMenuEscape(e: KeyboardEvent) {
  if (e.key === 'Escape' && activeMenu) {
    e.stopPropagation() // don't also close the wizard
    closeReviewMenu()
  }
}

function makeEl<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  cls: string,
  ...children: Array<Node | string>
): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag)
  if (cls) el.className = cls
  el.append(...children)
  return el
}

const boldEl = (text: string) => makeEl('b', '', text)
const dotEl = (scope: SuggestionScope) =>
  makeEl('span', `wiz-dot wiz-dot-${scope}`)
const quotedEl = (text: string) =>
  makeEl('span', 'wiz-decision-found', `“${text}”`)

function openReviewMenu(
  anchor: HTMLElement,
  s: TokenSuggestion,
  profile: WizardProfile,
  onChange: () => void
) {
  if (activeMenu?.suggestion === s) {
    closeReviewMenu()
    return
  }
  closeReviewMenu()

  const value = s.matchedText.trim()
  const menu = makeEl('div', 'wiz-pill-menu')

  const render = () => {
    menu.textContent = ''
    menu.appendChild(makeEl('div', 'wiz-menu-head', 'Fills in with…'))

    for (const scope of scopesFor(s.kind)) {
      const saved = savedValueFor(s.kind, scope, profile)?.trim()
      const hint =
        scope === 'agency'
          ? s.kind === 'name'
            ? `shows the agency’s current name (“${profile.agencyName}”)`
            : 'from your Agency profile'
          : 'each teammate shows their own'
      // Picking a choice that carries a follow-up control keeps the menu
      // open so the follow-up gets seen.
      const hasFollowUp =
        (scope === 'agency' && s.kind !== 'name' && saved && saved !== value) ||
        (scope === 'recruiter' && saved !== value)
      const item = makeEl(
        'div',
        s.choice === scope ? 'wiz-menu-item selected' : 'wiz-menu-item',
        dotEl(scope),
        makeEl('span', '', boldEl(scopeLabel(scope, s.kind)), ` — ${hint}`)
      )
      item.addEventListener('click', () => {
        s.choice = scope
        onChange()
        if (hasFollowUp) render()
        else closeReviewMenu()
      })
      menu.appendChild(item)
    }

    const keep = makeEl(
      'div',
      s.choice === 'text' ? 'wiz-menu-item selected' : 'wiz-menu-item',
      makeEl('span', '', boldEl('Keep as typed'), ' — ', quotedEl(value))
    )
    keep.addEventListener('click', () => {
      s.choice = 'text'
      onChange()
      closeReviewMenu()
    })
    menu.appendChild(keep)

    // Follow-ups for the CURRENT choice.
    const saved =
      s.choice !== 'text'
        ? savedValueFor(s.kind, s.choice, profile)?.trim()
        : undefined
    if (
      s.choice === 'agency' &&
      s.kind !== 'name' &&
      saved &&
      saved !== value
    ) {
      const opt = (label: string, selected: boolean, pick: () => void) => {
        const input = document.createElement('input')
        input.type = 'radio'
        input.checked = selected
        input.addEventListener('change', () => {
          pick()
          onChange()
          render()
        })
        return makeEl(
          'label',
          selected ? 'wiz-sub-opt selected' : 'wiz-sub-opt',
          input,
          label
        )
      }
      menu.appendChild(
        makeEl(
          'div',
          'wiz-menu-sub',
          makeEl('span', '', `Your profile has “${saved}” — `),
          opt(`update it to “${value}”`, s.updateSaved, () => {
            s.updateSaved = true
          }),
          opt(`keep “${saved}” (the header shows it)`, !s.updateSaved, () => {
            s.updateSaved = false
          })
        )
      )
    } else if (s.choice === 'recruiter' && saved !== value) {
      const input = document.createElement('input')
      input.type = 'checkbox'
      input.checked = s.saveAsMine
      input.addEventListener('change', () => {
        s.saveAsMine = input.checked
        onChange()
        render()
      })
      menu.appendChild(
        makeEl(
          'div',
          'wiz-menu-sub',
          makeEl(
            'label',
            'wiz-sub-opt',
            input,
            saved
              ? `this is mine — update my Profile (currently “${saved}”)`
              : 'this is mine — save it to my Profile'
          )
        )
      )
    }
  }

  render()
  // Measure, then place below the pill (above when out of room), clamped
  // to the viewport. position:fixed → immune to modal layout entirely.
  menu.style.left = '0px'
  menu.style.top = '0px'
  document.body.appendChild(menu)
  const rect = anchor.getBoundingClientRect()
  const size = menu.getBoundingClientRect()
  const left = Math.max(
    8,
    Math.min(rect.left, window.innerWidth - size.width - 8)
  )
  const top =
    rect.bottom + size.height + 8 > window.innerHeight
      ? rect.top - size.height - 4
      : rect.bottom + 4
  menu.style.left = `${left}px`
  menu.style.top = `${Math.max(8, top)}px`

  activeMenu = { el: menu, suggestion: s }
  document.addEventListener('mousedown', onOutsideMousedown, true)
  document.addEventListener('keydown', onMenuEscape, true)
  document.addEventListener('scroll', closeReviewMenu, true)
}

function renderModel(
  model: ExtractedHeaderModel,
  profile: WizardProfile,
  rev: { set: (n: number) => void; value: number }
) {
  const bump = () => rev.set(rev.value + 1)

  const dot = (scope: SuggestionScope) =>
    html.span(attr.class(`wiz-dot wiz-dot-${scope}`))

  /** The pill: current decision, click to open/close the change-menu. */
  const pill = (s: TokenSuggestion) => {
    const p = pillState(s, profile)
    return html.button(
      attr.type('button'),
      attr.class(p.linked ? 'wiz-pill linked' : 'wiz-pill'),
      on.click((e: MouseEvent) => {
        openReviewMenu(e.currentTarget as HTMLElement, s, profile, bump)
      }),
      p.scope ? dot(p.scope) : html.span(),
      html.b(p.label),
      p.note
        ? html.span(
            attr.class(p.warn ? 'wiz-pill-note warn' : 'wiz-pill-note'),
            ` · ${p.note}`
          )
        : html.span(),
      html.span(attr.class('wiz-pill-arrow'), '▾')
    )
  }

  const suggestionRow = (s: TokenSuggestion) =>
    html.div(attr.class('wiz-receipt-row'), quoted(s.matchedText), pill(s))

  return Fragment(
    html.div(
      attr.class('wiz-review-intro'),
      html.b('We found these details in your upload.'),
      ' Here’s what each one will do — anything matching your saved profiles is already linked. Click a decision to change it.'
    ),
    ...model.bands.map((band, bandIndex) =>
      html.div(
        attr.class('wiz-review-band'),
        html.div(
          attr.class('wiz-section-title'),
          band.region === 'header' ? 'Header' : 'Footer'
        ),
        ...band.images.map(image =>
          html.div(
            attr.class('wiz-logo-note'),
            html.img(
              attr.src(logoPreviewUrl(image.blob)),
              attr.alt('Your logo, cropped from the document')
            ),
            html.div(
              html.b('Your logo'),
              html.div(
                attr.class('wiz-hint'),
                'Cropped straight from your document, exactly as it appears here.'
              )
            )
          )
        ),
        ...band.blocks.map((block, blockIndex) =>
          html.div(
            attr.class(
              block.included ? 'wiz-review-card' : 'wiz-review-card excluded'
            ),
            html.div(
              attr.style(
                'display:flex; justify-content:space-between; align-items:flex-start; gap:10px;'
              ),
              html.div(
                attr.style('flex:1; min-width:0;'),
                ...block.lines.map((line, lineIndex) => {
                  const suggestions = model.suggestions.filter(
                    s =>
                      s.bandIndex === bandIndex &&
                      s.blockIndex === blockIndex &&
                      s.lineIndex === lineIndex
                  )
                  // A line that IS its single detection needs no separate
                  // context line — the row already shows the full text.
                  const rowsOnly =
                    suggestions.length === 1 &&
                    suggestions[0].matchedText.trim() === line.text.trim()
                  return Fragment(
                    rowsOnly
                      ? html.span()
                      : html.div(
                          attr.class('wiz-review-line'),
                          ...highlightMatches(line.text, suggestions)
                        ),
                    ...suggestions.map(suggestionRow)
                  )
                })
              ),
              html.button(
                attr.type('button'),
                attr.class('wiz-btn-link'),
                on.click(() => {
                  block.included = !block.included
                  bump()
                }),
                block.included ? 'Exclude' : 'Include'
              )
            )
          )
        )
      )
    )
  )
}

/** Renders the line with each detected substring visually highlighted. */
function highlightMatches(text: string, suggestions: TokenSuggestion[]) {
  const ranges = suggestions
    .map(s => {
      const index = text.indexOf(s.matchedText)
      return index >= 0 ? { index, length: s.matchedText.length } : null
    })
    .filter((r): r is { index: number; length: number } => r !== null)
    .sort((a, b) => a.index - b.index)

  const parts: ReturnType<typeof html.span>[] = []
  let cursor = 0
  for (const r of ranges) {
    if (r.index < cursor) continue
    if (r.index > cursor) parts.push(html.span(text.slice(cursor, r.index)))
    parts.push(
      html.span(
        attr.class('wiz-line-match'),
        text.slice(r.index, r.index + r.length)
      )
    )
    cursor = r.index + r.length
  }
  if (cursor < text.length) parts.push(html.span(text.slice(cursor)))
  return parts
}
